<template>
  <div
    v-if="hasEntries"
    class="winter-service-log-history"
  >
    <div class="winter-service-log-history__title">
      {{ translate(`${translationNamespace}.log_history`) }}
    </div>

    <!--    One grid for the whole box rather than one per row, so both entries'
            timestamps sit in the same column however long the label beside them is.-->
    <div class="winter-service-log-history__box">
      <template
        v-if="createdAt"
      >
        <span class="winter-service-log-history__label">
          {{ translate(`${translationNamespace}.data_entry`) }}
        </span>

        <span class="winter-service-log-history__value">
          {{ createdAt }}
        </span>
      </template>

      <template
        v-if="updatedAt"
      >
        <span class="winter-service-log-history__label">
          {{ translate(`${translationNamespace}.data_modification`) }}
        </span>

        <span class="winter-service-log-history__value">
          {{ updatedAt }}
        </span>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useTranslate } from '@/global/composables/useTranslate'
import { dateService, FORMATS } from '@/global/services/date'

/**
 * The history box a winter service details screen closes with - the task's and the work
 * order's alike, which is why it lives here rather than beside either of them.
 *
 * The endpoints report when the record was entered and when it was last changed, but not
 * by whom, so the rows carry the timestamps only - the design has a name column between
 * the two, waiting on the backend.
 *
 * Each screen keeps its own translations, so the namespace the three labels are read
 * from is handed in rather than fixed here.
 */
const props = defineProps({
  record: {
    type: Object,
    default: () => ({})
  },
  translationNamespace: {
    type: String,
    required: true
  }
})

const { translate } = useTranslate()

const toDatetimeLabel = (value) => value
  ? dateService.formatCustom(value, FORMATS.isoDatetime, FORMATS.datetimeShort)
  : ''

const createdAt = computed(() => toDatetimeLabel(props.record?.created_at))
const updatedAt = computed(() => toDatetimeLabel(props.record?.updated_at))

const hasEntries = computed(() => !!(createdAt.value || updatedAt.value))
</script>

<style scoped>
.winter-service-log-history__title {
  font-family: 'Satoshi', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 8px;
}

.winter-service-log-history__box {
  display: grid;
  grid-template-columns: max-content max-content;
  column-gap: 24px;
  row-gap: 4px;
  justify-content: start;
  padding: 12px 20px;
  border: 1px solid #E5E7EB;
  border-radius: 16px;
  box-sizing: border-box;
  font-size: 13px;
}

/* Only the two labels are bolded. The design has the whole row in one weight, but a
   timestamp is not what the eye should land on first. */
.winter-service-log-history__label {
  font-weight: 700;
  color: #111827;
}

.winter-service-log-history__value {
  color: #374151;
}
</style>
